import cronstrue from 'cronstrue';

export const CRON_FREQUENCY_OPTIONS = [
  { value: 'every_minute', label: 'Every minute' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom expression' },
];

export const DEFAULT_CRON_BUILDER = {
  frequency: 'daily',
  minute: 0,
  hour: 9,
  dayOfWeek: 1,
  dayOfMonth: 1,
};

export const describeCron = (cronExpression) => {
  if (!cronExpression) return '';
  try {
    return cronstrue.toString(cronExpression, {
      use24HourTimeFormat: true,
      throwExceptionOnParseError: true,
    });
  } catch (error) {
    return '';
  }
};

export const buildCronFromBuilder = (builder, customExpression = '') => {
  if (!builder) return customExpression;
  if (builder.frequency === 'custom') {
    return customExpression.trim();
  }

  const minute = Number(builder.minute) || 0;
  const hour = Number(builder.hour) || 0;
  const dayOfWeek = Number(builder.dayOfWeek) || 0;
  const dayOfMonth = Number(builder.dayOfMonth) || 1;

  switch (builder.frequency) {
    case 'every_minute':
      return '0 * * * * *';
    case 'hourly':
      return `0 ${minute} * * * *`;
    case 'daily':
      return `0 ${minute} ${hour} * * *`;
    case 'weekly':
      return `0 ${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `0 ${minute} ${hour} ${dayOfMonth} * *`;
    default:
      return customExpression.trim();
  }
};

/** Map a 6-field cron string back to builder state, or `{ frequency: 'custom' }`. */
export const inferBuilderFromCron = (cronExpression) => {
  const fields = String(cronExpression || '').trim().split(/\s+/).filter(Boolean);
  if (fields.length < 6) {
    return { ...DEFAULT_CRON_BUILDER, frequency: 'custom' };
  }

  const [sec, min, hour, dom, mon, dow] = fields;

  if (sec === '0' && min === '*' && hour === '*' && dom === '*' && mon === '*' && dow === '*') {
    return { ...DEFAULT_CRON_BUILDER, frequency: 'every_minute' };
  }
  if (sec === '0' && /^\d+$/.test(min) && hour === '*' && dom === '*' && mon === '*' && dow === '*') {
    return { ...DEFAULT_CRON_BUILDER, frequency: 'hourly', minute: Number(min) };
  }
  if (sec === '0' && /^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && mon === '*' && dow === '*') {
    return { ...DEFAULT_CRON_BUILDER, frequency: 'daily', minute: Number(min), hour: Number(hour) };
  }
  if (sec === '0' && /^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && mon === '*' && /^\d+$/.test(dow)) {
    return {
      ...DEFAULT_CRON_BUILDER,
      frequency: 'weekly',
      minute: Number(min),
      hour: Number(hour),
      dayOfWeek: Number(dow),
    };
  }
  if (sec === '0' && /^\d+$/.test(min) && /^\d+$/.test(hour) && /^\d+$/.test(dom) && mon === '*' && dow === '*') {
    return {
      ...DEFAULT_CRON_BUILDER,
      frequency: 'monthly',
      minute: Number(min),
      hour: Number(hour),
      dayOfMonth: Number(dom),
    };
  }

  return { ...DEFAULT_CRON_BUILDER, frequency: 'custom' };
};

const parseDateTimeValue = (value) => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(value);

  const trimmed = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const looksLikeIsoDateTime = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(trimmed);

  return new Date(looksLikeIsoDateTime && !hasTimezone ? `${trimmed}Z` : trimmed);
};

export const formatDateTime = (value, timezone) => {
  if (!value) return '-';
  try {
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };

    if (timezone && !/^UTC\s*[+-]/i.test(timezone)) {
      options.timeZone = timezone;
    }

    return parseDateTimeValue(value).toLocaleString(undefined, {
      ...options,
    });
  } catch (error) {
    return parseDateTimeValue(value).toLocaleString();
  }
};

export const getLocalUtcOffsetLabel = (date = new Date()) => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;

  return `UTC ${sign} ${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

export const getUtcOffsetLabelForTimezone = (timezone, date = new Date()) => {
  if (!timezone) return getLocalUtcOffsetLabel(date);
  if (/^UTC\s*[+-]/i.test(timezone)) {
    return timezone.replace(/^UTC\s*([+-])\s*/i, 'UTC $1 ');
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
    }).formatToParts(date);
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (!offsetPart || offsetPart === 'GMT') return 'UTC +0';

    return offsetPart.replace(/GMT([+-])/, 'UTC $1 ');
  } catch {
    return getLocalUtcOffsetLabel(date);
  }
};
